import React from 'react';
import withStyles from 'isomorphic-style-loader/withStyles';
import axios from 'axios';
import s from './CompanyAuth.css';
import { sendVerificationSecureSms, verifyPhoneNumber, createUser } from '../../lib/graphql';
import { setCookies } from '../../helpers/Cookies/cookies';
import { encode } from '../../helpers/queryEncryption';
import { clientSmsKey } from '../../helpers/clientSmsKey';

// ============================================================================
// CompanyAuth — DETACHED company-facing auth surface for /become-a-company.
//
// This is intentionally SEPARATE from the client `/login` + `/signup` flow and
// from the shared header login/register modals. Companies get their own polished
// entry (landing + signup + sign-in). Those client surfaces are left UNTOUCHED.
//
// A "company account" is just a User (besthand-api flips accountType -> COMPANY
// inside createCompanyProfile, the wizard's first step). So here we simply create
// / log in a normal User and drop an `id_token` cookie — the SAME mechanism the
// client web uses (createUser -> result.token; verifyPhoneNumber -> result.token,
// see src/components/Login/RegisterForm/submit.js and src/actions/login/*). The
// `/lp-graphql` proxy then forwards that cookie to besthand-api as `authToken`.
//
// NOTE(no-password): the BestHand User account has NO password column — web user
// auth is phone + SMS OTP (sendVerificationSecureSms -> verifyPhoneNumber). We
// therefore reuse that proven mechanism rather than inventing a password flow.
// "Sign in" = phone + OTP; "Create account" = details + phone + OTP -> createUser.
// On success we set the id_token cookie and hand control back to the wizard via
// the onAuthenticated() callback.
// ============================================================================

// graphql-tag documents for the SMS/OTP login (mirror src/actions/login/*).
// createUser is a plain query string (see lib/graphql/user.js) posted to /graphql.

const US_STATES = [
  'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA', 'HI', 'ID', 'IL', 'IN', 'IA',
  'KS', 'KY', 'LA', 'ME', 'MD', 'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ',
  'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC', 'SD', 'TN', 'TX', 'UT', 'VT',
  'VA', 'WA', 'WV', 'WI', 'WY', 'DC',
];

// userType 1 = customer/user (companies are users with accountType later flipped
// to COMPANY by createCompanyProfile). 2 = worker — never used here.
const USER_TYPE = 1;

// COMPLIANCE (CCPA/CPRA + ESIGN): version string recorded against the ToS/Privacy
// consent captured at company signup. Mirrors the client RegisterForm constant
// (src/components/Login/RegisterForm/submit.js). Bump when the legal copy changes.
const LEGAL_CONSENT_VERSION = '2026-06';

// "How did you hear about us?" — optional marketing attribution captured at
// signup and persisted on the User (acquisitionSource) via createUser.
const ACQUISITION_OPTIONS = [
  ['GOOGLE', 'Google / search engine'],
  ['REFERRAL', 'Friend or referral'],
  ['SOCIAL', 'Social media'],
  ['AD', 'Online ad'],
  ['BLOG', 'News / blog'],
  ['OTHER', 'Other'],
];

const Icon = ({ d }) => (
  <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path d={d} stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const VALUE_PROPS = [
  {
    d: 'M12 2 4 6v6c0 5 3.5 8 8 10 4.5-2 8-5 8-10V6l-8-4Z',
    title: 'Verified once, trusted everywhere',
    body: 'Submit your licenses, insurance, EIN, and owner ID a single time. We verify your company so clients know you’re legit.',
  },
  {
    d: 'M3 7h18M3 12h18M3 17h12',
    title: 'Get matched to big jobs',
    body: 'Receive large home-improvement and construction project requests across your service area, then quote with confidence.',
  },
  {
    d: 'M3 10h18M5 6h14a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2Z',
    title: 'Get paid securely',
    body: 'Payouts run through Stripe Connect — milestones, escrow-style protection, and on-time deposits to your bank.',
  },
];

// Trust signals shown under the hero CTAs. Each maps onto a real BestHand
// differentiator over lead-gen marketplaces (no per-lead charges, KYB
// verification, escrow-style milestone payouts).
const TRUST_SIGNALS = [
  { d: 'M20 6 9 17l-5-5', label: 'No lead fees — ever' },
  { d: 'M12 2 4 6v6c0 5 3.5 8 8 10 4.5-2 8-5 8-10V6l-8-4Z', label: 'License & insurance verified' },
  { d: 'M12 1v22M5 5h11a3 3 0 0 1 0 6H8a3 3 0 0 0 0 6h11', label: 'Escrow-protected payouts' },
];

// "How it works" — a short, confident three-step path mirroring Thumbtack’s
// onboarding clarity but emphasizing our verification + secure payouts.
const STEPS_HOW = [
  { n: '1', title: 'Build your profile', body: 'Logo, services, team, and where you work — done in minutes.' },
  { n: '2', title: 'Get verified', body: 'Upload your license, insurance, and ID. We review every document.' },
  { n: '3', title: 'Win & get paid', body: 'Quote on matched projects and receive milestone payouts via Stripe.' },
];

class CompanyAuth extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      // view: 'landing' | 'signup' | 'signin'
      view: 'landing',
      // signup/signin phase: 'details' (signup only) -> 'phone' -> 'otp'
      phase: 'phone',
      saving: false,
      error: '',
      notice: '',
      // signup details
      companyName: '',
      firstName: '',
      lastName: '',
      email: '',
      acquisitionSource: '', // optional "How did you hear about us?"
      // COMPLIANCE: explicit ToS + Privacy acceptance, required before signup.
      acceptedLegal: false,
      // shared phone
      phoneDialCode: '+1',
      phoneNumber: '',
      // otp
      otp: '',
    };
  }

  setField(field, value) {
    this.setState({ [field]: value, error: '' });
  }

  go(view) {
    // signup starts on the details step; signin jumps straight to phone entry.
    this.setState({
      view,
      phase: view === 'signup' ? 'details' : 'phone',
      error: '',
      notice: '',
      otp: '',
    });
  }

  // ---- step transitions ----
  detailsValid() {
    const { companyName, email, phoneNumber, acceptedLegal } = this.state;
    const emailOk = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test((email || '').trim());
    return (
      (companyName || '').trim().length >= 2 &&
      emailOk &&
      (phoneNumber || '').replace(/\D/g, '').length >= 7 &&
      // COMPLIANCE: ToS + Privacy must be accepted before we can continue.
      !!acceptedLegal
    );
  }

  phoneValid() {
    return (this.state.phoneNumber || '').replace(/\D/g, '').length >= 7;
  }

  // Send the SMS verification code. jwtToken is NonNull on the api but logged-out
  // callers pass '' (matches the client login action's contract).
  async sendOtp() {
    const { phoneDialCode, phoneNumber } = this.state;
    this.setState({ saving: true, error: '', notice: '' });
    try {
      const res = await axios.post('/graphql', {
        query: sendVerificationSecureSms.loc.source.body,
        variables: {
          phoneDialCode,
          phoneNumber: phoneNumber.trim(),
          userType: USER_TYPE,
          jwtToken: encode(clientSmsKey),
        },
      }, { headers: { Accept: 'application/json', 'Content-Type': 'application/json' }, withCredentials: true });
      const env = res.data && res.data.data && res.data.data.sendVerificationSecureSms;
      if (env && env.status === 200) {
        this.setState({ saving: false, phase: 'otp', notice: 'We texted you a verification code.' });
      } else {
        this.setState({ saving: false, error: (env && env.errorMessage) || 'Could not send the code. Check the number and try again.' });
      }
    } catch (e) {
      this.setState({ saving: false, error: `Could not send the code: ${e.message}` });
    }
  }

  // Verify the OTP. For an EXISTING user this returns result.token -> we set the
  // cookie and proceed. For a NEW user (sign-up) verifyPhoneNumber returns
  // result.auth === '' (no account yet) -> we finalize with createUser.
  async verifyOtp() {
    const { view, phoneDialCode, phoneNumber, otp } = this.state;
    this.setState({ saving: true, error: '', notice: '' });
    try {
      const res = await axios.post('/graphql', {
        query: verifyPhoneNumber.loc.source.body,
        variables: {
          phoneDialCode,
          phoneNumber: phoneNumber.trim(),
          verificationCode: Number(otp),
          userType: USER_TYPE,
        },
      }, { headers: { Accept: 'application/json', 'Content-Type': 'application/json' }, withCredentials: true });
      const env = res.data && res.data.data && res.data.data.verifyPhoneNumber;
      if (!env || env.status !== 200) {
        this.setState({ saving: false, error: (env && env.errorMessage) || 'That code didn’t work. Please try again.' });
        return;
      }
      const result = env.result || {};
      // Existing account: token present -> log straight in.
      if (result.token) {
        this.authenticate(result.token, result.expiresIn);
        return;
      }
      // No account yet (result.auth === ''). On the sign-in tab that means the
      // number isn't registered — guide them to sign up. On the sign-up tab we
      // create the company user now.
      if (view === 'signin') {
        this.setState({ saving: false, error: 'No company account found for that number. Create one to get started.' });
        return;
      }
      await this.createCompanyUser();
    } catch (e) {
      this.setState({ saving: false, error: `Verification failed: ${e.message}` });
    }
  }

  // createUser — the SAME mutation the client RegisterForm uses. The legal/company
  // name maps onto the User firstName/lastName (companyProfile.legalName is set
  // properly later in the wizard's createCompanyProfile step). Returns result.token.
  async createCompanyUser() {
    const { companyName, firstName, lastName, email, phoneDialCode, phoneNumber, acquisitionSource, acceptedLegal } = this.state;
    // COMPLIANCE: defence-in-depth — never create the account without consent
    // (the Continue button is also gated on acceptedLegal via detailsValid()).
    if (!acceptedLegal) {
      this.setState({ saving: false, error: 'You must accept the Terms of Service and Privacy Policy to continue.' });
      return;
    }
    try {
      const res = await axios.post('/graphql', {
        query: createUser,
        variables: {
          firstName: (firstName || companyName || '').trim() || companyName.trim(),
          lastName: (lastName || '').trim(),
          email: email.trim(),
          phoneDialCode,
          phoneNumber: phoneNumber.trim(),
          phoneCountryCode: 'US',
          userType: USER_TYPE,
          acquisitionSource: acquisitionSource || null,
          // COMPLIANCE (CCPA/CPRA + ESIGN): record the accepted ToS/Privacy versions.
          acceptedTosVersion: LEGAL_CONSENT_VERSION,
          acceptedPrivacyVersion: LEGAL_CONSENT_VERSION,
        },
      }, { headers: { Accept: 'application/json', 'Content-Type': 'application/json' }, withCredentials: true });
      const env = res.data && res.data.data && res.data.data.createUser;
      if (env && env.status === 200 && env.result && env.result.token) {
        this.authenticate(env.result.token, env.result.expiresIn);
      } else {
        this.setState({ saving: false, error: (env && env.errorMessage) || 'We couldn’t create your account. Please try again.' });
      }
    } catch (e) {
      this.setState({ saving: false, error: `Account creation failed: ${e.message}` });
    }
  }

  async authenticate(token, expiresIn) {
    // SECURITY: set the id_token cookie SERVER-SIDE as httpOnly (XSS can't read it); the
    // /lp-graphql proxy reads it and forwards to besthand-api. Falls back to a client
    // cookie only if the server call fails, so onboarding is never blocked.
    const _ttl = (expiresIn || 60 * 60 * 24 * 180);
    try {
      const r = await fetch('/auth/session', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({ token, maxAge: _ttl }),
      });
      if (!r.ok) throw new Error('session set failed');
    } catch (e) {
      setCookies({ name: 'id_token', value: token, options: { path: '/', maxAge: _ttl, secure: true, sameSite: 'lax' } });
    }
    this.setState({ saving: false, notice: 'You’re in! Loading your onboarding…' });
    if (typeof this.props.onAuthenticated === 'function') {
      this.props.onAuthenticated();
    }
  }

  // ---- render ----
  render() {
    const { view } = this.state;
    return (
      <div className={s.page}>
        {view === 'landing' ? this.renderLanding() : this.renderAuthCard()}
      </div>
    );
  }

  renderLanding() {
    return (
      <div>
        <section className={s.hero}>
          <div className={s.wrap}>
            <span className={s.eyebrow}>BestHand Large Projects · for companies</span>
            <h1 className={s.h1}>
              Win big renovation projects — get matched, quote, and{' '}
              <span className={s.hl}>get paid securely</span> on BestHand.
            </h1>
            <p className={s.sub}>
              Bring your construction or home-improvement company onto BestHand. Verify once,
              then receive large project requests across your service area and run them end to
              end — quotes, contracts, and milestone payouts.
            </p>
            <div className={s.ctaRow}>
              <button type="button" className={s.btn} onClick={() => this.go('signup')}>
                Create a company account
              </button>
              <button type="button" className={s.btnGhost} onClick={() => this.go('signin')}>
                Company sign in
              </button>
            </div>
            <div className={s.trustRow}>
              {TRUST_SIGNALS.map((t) => (
                <span className={s.trustItem} key={t.label}>
                  <Icon d={t.d} />
                  {t.label}
                </span>
              ))}
            </div>
          </div>
        </section>

        <section className={s.valueWrap}>
          <div className={s.wrap}>
            <div className={s.valueGrid}>
              {VALUE_PROPS.map((v) => (
                <div className={s.valueCard} key={v.title}>
                  <div className={s.valueIc}><Icon d={v.d} /></div>
                  <h3 className={s.valueTitle}>{v.title}</h3>
                  <p className={s.valueBody}>{v.body}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className={s.howWrap}>
          <div className={s.wrap}>
            <h2 className={s.howTitle}>How it works</h2>
            <div className={s.howGrid}>
              {STEPS_HOW.map((h) => (
                <div className={s.howCard} key={h.n}>
                  <span className={s.howNo}>{h.n}</span>
                  <h3 className={s.howCardTitle}>{h.title}</h3>
                  <p className={s.howCardBody}>{h.body}</p>
                </div>
              ))}
            </div>
            <div className={s.howCta}>
              <button type="button" className={s.btn} onClick={() => this.go('signup')}>
                Create a company account
              </button>
              <button type="button" className={s.btnText} onClick={() => this.go('signin')}>
                Already registered? Sign in
              </button>
            </div>
          </div>
        </section>
      </div>
    );
  }

  renderAlerts() {
    const { error, notice } = this.state;
    return (
      <div>
        {error ? <div className={s.alertErr}>{error}</div> : null}
        {notice ? <div className={s.alertOk}>{notice}</div> : null}
      </div>
    );
  }

  renderAuthCard() {
    const { view } = this.state;
    const isSignup = view === 'signup';
    return (
      <section className={s.authSection}>
        <div className={s.authWrap}>
          <div className={s.authCard}>
            <span className={s.eyebrowSm}>BestHand · for companies</span>
            <h2 className={s.authTitle}>
              {isSignup ? 'Create your company account' : 'Company sign in'}
            </h2>
            <p className={s.authHint}>
              {isSignup
                ? 'A few details to get started. We’ll verify your phone, then walk you through onboarding.'
                : 'Sign in with the phone number on your company account.'}
            </p>

            {this.renderAlerts()}
            {isSignup ? this.renderSignup() : this.renderSignin()}

            <div className={s.switchRow}>
              {isSignup ? (
                <span>
                  Already have a company account?{' '}
                  <button type="button" className={s.linkBtn} onClick={() => this.go('signin')}>Sign in</button>
                </span>
              ) : (
                <span>
                  New to BestHand?{' '}
                  <button type="button" className={s.linkBtn} onClick={() => this.go('signup')}>Create a company account</button>
                </span>
              )}
              <button type="button" className={s.linkBtn} onClick={() => this.go('landing')}>← Back</button>
            </div>
          </div>
        </div>
      </section>
    );
  }

  renderSignup() {
    const { phase, saving, companyName, firstName, lastName, email, acquisitionSource, acceptedLegal } = this.state;
    if (phase === 'details') {
      return (
        <div>
          <div className={s.formGrid}>
            <label className={s.fieldFull}>
              <span>Company name *</span>
              <input className={s.input} value={companyName}
                onChange={(e) => this.setField('companyName', e.target.value)}
                placeholder="Acme Construction LLC" />
            </label>
            <label className={s.field}>
              <span>Your first name</span>
              <input className={s.input} value={firstName}
                onChange={(e) => this.setField('firstName', e.target.value)}
                placeholder="Jordan" />
            </label>
            <label className={s.field}>
              <span>Your last name</span>
              <input className={s.input} value={lastName}
                onChange={(e) => this.setField('lastName', e.target.value)}
                placeholder="Rivera" />
            </label>
            <label className={s.fieldFull}>
              <span>Work email *</span>
              <input className={s.input} type="email" value={email}
                onChange={(e) => this.setField('email', e.target.value)}
                placeholder="you@company.com" />
            </label>
            <label className={s.fieldFull}>
              <span>How did you hear about us?</span>
              <select className={s.input} value={acquisitionSource}
                onChange={(e) => this.setField('acquisitionSource', e.target.value)}>
                <option value="">Select an option (optional)</option>
                {ACQUISITION_OPTIONS.map(([v, label]) => (
                  <option key={v} value={v}>{label}</option>
                ))}
              </select>
            </label>
          </div>
          {this.renderPhoneFields()}
          {/* COMPLIANCE (CCPA/CPRA + ESIGN): required ToS + Privacy acceptance,
              mirrors the client RegisterForm consent checkbox. Gates Continue. */}
          <label className={s.legalConsent}
            style={{ display: 'flex', alignItems: 'flex-start', gap: 10, margin: '14px 0 4px', fontSize: 13, lineHeight: 1.4, cursor: 'pointer' }}>
            <input type="checkbox" checked={acceptedLegal}
              onChange={(e) => this.setField('acceptedLegal', e.target.checked)}
              style={{ marginTop: 2, flex: '0 0 auto' }} />
            <span>
              I agree to BestHand’s{' '}
              <a href="/terms" target="_blank" rel="noopener noreferrer">Terms of Service</a>{' '}and{' '}
              <a href="/privacy-policy" target="_blank" rel="noopener noreferrer">Privacy Policy</a>.
            </span>
          </label>
          <div className={s.authActions}>
            <button type="button" className={s.btn}
              disabled={saving || !this.detailsValid()}
              onClick={() => this.sendOtp()}>
              {saving ? 'Sending code…' : 'Continue'}
            </button>
          </div>
        </div>
      );
    }
    return this.renderOtp();
  }

  renderSignin() {
    const { phase, saving } = this.state;
    if (phase === 'otp') return this.renderOtp();
    return (
      <div>
        {this.renderPhoneFields()}
        <div className={s.authActions}>
          <button type="button" className={s.btn}
            disabled={saving || !this.phoneValid()}
            onClick={() => this.sendOtp()}>
            {saving ? 'Sending code…' : 'Send verification code'}
          </button>
        </div>
      </div>
    );
  }

  renderPhoneFields() {
    const { phoneDialCode, phoneNumber } = this.state;
    return (
      <div className={s.formGrid}>
        <label className={s.field} style={{ maxWidth: 120 }}>
          <span>Dial code</span>
          <input className={s.input} value={phoneDialCode}
            onChange={(e) => this.setField('phoneDialCode', e.target.value)}
            placeholder="+1" />
        </label>
        <label className={s.field}>
          <span>Mobile phone *</span>
          <input className={s.input} type="tel" value={phoneNumber}
            onChange={(e) => this.setField('phoneNumber', e.target.value)}
            placeholder="555 123 4567" />
        </label>
      </div>
    );
  }

  renderOtp() {
    const { saving, otp, phoneDialCode, phoneNumber } = this.state;
    return (
      <div>
        <p className={s.authHint}>
          Enter the code we sent to <strong>{phoneDialCode} {phoneNumber}</strong>.
        </p>
        <label className={s.fieldFull}>
          <span>Verification code *</span>
          <input className={s.input} inputMode="numeric" value={otp}
            onChange={(e) => this.setField('otp', e.target.value.replace(/\D/g, ''))}
            placeholder="4-digit code" maxLength={4} />
        </label>
        <div className={s.authActions}>
          <button type="button" className={s.btn}
            disabled={saving || (otp || '').length < 4}
            onClick={() => this.verifyOtp()}>
            {saving ? 'Verifying…' : 'Verify & continue'}
          </button>
          <button type="button" className={s.btnText}
            disabled={saving}
            onClick={() => this.sendOtp()}>
            Resend code
          </button>
        </div>
      </div>
    );
  }
}

export default withStyles(s)(CompanyAuth);
